import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { appleAuthHelpers, useScript } from 'react-apple-signin-auth';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { layoutDefaults } from '../../utils/layout-defaults';

const animationEnter = 'animate__animated animate__bounceIn animate__delay-1s';
const animationButton =
  'animate__animated animate__pulse animate__faster animate__delay-1s';
const delay1 = '1.2s';
const delay2 = '1.25s';
const service = 'apple';

function OAuthAppleButton({
  logo,
  analytics,
  accountAPI,
  config,
  onLoggedIn,
  onErrorMessage,
  children,
  className,
}) {
  const { analyticsFeatureUseEvent } = analytics;
  const [appleFail, setAppleFail] = useState(false);
  const { appleSigninFromRemote } = accountAPI;
  const [loading, setLoading] = useState(false);
  const classes = useStyles();
  const { t } = useTranslation();

  useScript(appleAuthHelpers.APPLE_SCRIPT_SRC);

  useEffect(() => {
    // eslint-disable-next-line no-undef
    document.addEventListener('AppleIDSignInOnSuccess', onLoginSuccess);
    // eslint-disable-next-line no-undef
    document.addEventListener('AppleIDSignInOnFailure', onLoginFailure);

    return () => {
      // eslint-disable-next-line no-undef
      document.removeEventListener('AppleIDSignInOnSuccess', onLoginSuccess);
      // eslint-disable-next-line no-undef
      document.removeEventListener('AppleIDSignInOnFailure', onLoginFailure);
    };
  }, []);

  async function onLoginSuccess(pResponse) {
    // https://github.com/A-Tokyo/react-apple-signin-auth
    // https://dev.to/heyitsarpit/how-to-add-signin-with-apple-on-your-website-43m9
    // https://developer.apple.com/documentation/sign_in_with_apple/sign_in_with_apple_js/configuring_your_webpage_for_sign_in_with_apple
    // https://developer.apple.com/documentation/sign_in_with_apple/configuring_your_environment_for_sign_in_with_apple
    // https://github.com/mikereem/apple-signin

    // Para atualizar o ícone:
    // https://stackoverflow.com/questions/58475605/how-can-i-set-a-logo-in-the-sign-in-with-apple-consent-screen

    try {
      const data = {
        token_id: pResponse.detail?.authorization?.id_token,
        code: pResponse.detail?.authorization?.code,
        name: pResponse.detail?.user?.name?.firstName,
      };

      const response = await appleSigninFromRemote(data);
      await onLoggedIn?.call(this, response.data, service);
    } catch (error) {
      if (error.response?.status === 422) {
        const validation = error.response?.data?.find(() => true);
        onErrorMessage?.call(this, validation.message, service);
      } else {
        onErrorMessage?.call(this, t('oauth.apple.error'), service);
      }
    }

    setLoading(false);
  }

  function onLoginFailure(event) {
    if (event.detail?.error !== 'popup_closed_by_user') {
      setAppleFail(true);
      onErrorMessage?.call(this, t('oauth.apple.error'), service);
      console.error(event);
    } else {
      onErrorMessage?.call(this, t('oauth.apple.login-canceled'), service);
      console.error(event);
    }

    setLoading(false);
  }

  const onClick = () => {
    analyticsFeatureUseEvent('auth apple click');
    onErrorMessage?.call(this, null, service);
    setLoading(true);

    setTimeout(() => {
      appleAuthHelpers.signIn({
        authOptions: {
          clientId: config.auth?.apple?.client_id,
          scope: 'name email',
          redirectURI: 'https://menur.app',
          usePopup: true,
        },
      });
    }, 100);
  };

  return (
    <Button
      style={{ backgroundColor: '#fffafa', '--animate-delay': delay2 }}
      className={classNames(className, classes.container, animationButton)}
      size="md"
      disabled={appleFail || loading}
      onClick={onClick}
    >
      <div className={classes.button}>
        <div className={classes.buttonLeft}>
          <img
            src={logo}
            className={classNames(classes.logo, animationEnter)}
            style={{ '--animate-delay': delay1 }}
            alt="website logo"
          />
          {children}
        </div>
        {loading && <Spinner animation="border" className={classes.spinner} />}
      </div>
    </Button>
  );
}

const useStyles = createUseStyles({
  container: {
    width: '100%',
    // backgroundColor: "white",
    textAlign: 'left',
    // boxShadow: `0px 2px px #777`,
  },
  button: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  buttonLeft: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  logo: {
    height: 28,
    width: 28,
    marginRight: layoutDefaults.smallHorizontalMargin,
    // border: "1px solid red",
  },
  spinner: {
    color: layoutDefaults.secondaryColor,
    fontSize: layoutDefaults.fontSize,
    height: 26,
    width: 26,
  },
});

export { OAuthAppleButton };
